import React, { useEffect, useMemo, useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useCart } from "../../context/CartContext";
import { ShippingRate } from "./ShippingForm";
import { useNavigate } from "react-router-dom";
import {
  saveOrderToSupabase,
  CartItem as SupabaseCartItem,
  ShippingData as SupabaseShippingData,
  StripeTotals,
} from "./saveOrderToSupabase";

const CARD_OPTIONS = {
  style: {
    base: {
      color: "#4C1D95",
      fontSize: "16px",
      fontFamily: "Poppins, sans-serif",
      "::placeholder": { color: "#A78BFA" },
    },
    invalid: { color: "#E11D48" },
  },
};

type Props = {
  clientSecret?: string;
};

const centsToDollars = (cents: number) => (cents / 100).toFixed(2);

const StripeCheckoutForm: React.FC<Props> = ({ clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();

  const [cardName, setCardName] = useState("");
  const [shippingRate, setShippingRate] = useState<ShippingRate | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totals, setTotals] = useState<StripeTotals | null>(null);

  useEffect(() => {
    const storedRate = localStorage.getItem("shippingRate");
    if (storedRate) {
      try {
        setShippingRate(JSON.parse(storedRate));
      } catch {
        // ignore
      }
    }

    const storedTotals = localStorage.getItem("stripeTotals");
    if (storedTotals) {
      try {
        setTotals(JSON.parse(storedTotals));
      } catch {
        // ignore
      }
    }
  }, []);

  const resolvedClientSecret =
    clientSecret || localStorage.getItem("stripeClientSecret") || "";

  const displayTotal = useMemo(() => {
    if (totals?.total_cents != null) return centsToDollars(totals.total_cents);
    return "—";
  }, [totals]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    if (!resolvedClientSecret) {
      setMessage("Missing payment session. Please go back and try again.");
      return;
    }

    if (!totals) {
      setMessage("Missing Stripe totals. Please go back and recalculate shipping.");
      return;
    }

    if (!shippingRate?.object_id) {
      setMessage("Please select a shipping rate first.");
      return;
    }

    setIsLoading(true);
    setMessage(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setMessage("Card input is not available.");
      setIsLoading(false);
      return;
    }

    try {
      // ✅ Pay Stripe
      const result = await stripe.confirmCardPayment(resolvedClientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { name: cardName },
        },
      });

      if (result.error) {
        setMessage(result.error.message || "Payment failed.");
        return;
      }

      if (result.paymentIntent?.status !== "succeeded") {
        setMessage("Payment was not successful.");
        return;
      }

      // ✅ Read shipping data from localStorage (created in ShippingStep)
      const shippingDataStr = localStorage.getItem("shippingData");
      if (!shippingDataStr) {
        throw new Error("Missing shipping data. Please go back and try again.");
      }

      const raw = JSON.parse(shippingDataStr);

      const to_name = raw.to_name ?? raw.fullName ?? "";
      const to_address = raw.to_address ?? raw.address ?? "";
      const to_city = raw.to_city ?? raw.city ?? "";
      const to_state = raw.to_state ?? raw.state ?? "";
      const to_zip = raw.to_zip ?? raw.zip ?? "";
      const to_country = raw.to_country ?? raw.country ?? "US";
      const email = raw.email ?? null;
      const phone_number = raw.phone_number ?? raw.phone ?? null;

      const rate_object_id = raw.rate_object_id ?? shippingRate.object_id;

      if (
        !to_name ||
        !to_address ||
        !to_city ||
        !to_state ||
        !to_zip ||
        !email ||
        !phone_number
      ) {
        throw new Error(
          "Shipping info is incomplete. Please go back and re-enter your shipping details."
        );
      }

      if (!rate_object_id) {
        throw new Error("Missing shipping rate. Please reselect a shipping rate.");
      }

      // Prefer shippingData.shipping_rate, then raw.rate, then shippo selectedRate.amount
      const shippingRateDollars =
        typeof raw.shipping_rate === "number"
          ? raw.shipping_rate
          : typeof raw.rate === "number"
          ? raw.rate
          : shippingRate?.amount
          ? Number(shippingRate.amount)
          : 0;

      const shippingDataToSave: SupabaseShippingData = {
        to_name,
        to_address,
        to_city,
        to_state,
        to_zip,
        to_country,
        email,
        phone_number,
        rate_object_id,
        shipping_rate: Number.isFinite(shippingRateDollars) ? shippingRateDollars : null,
        carrier: raw.carrier ?? shippingRate.provider ?? null,
        label_url: raw.label_url ?? null,
        tracking_number: raw.tracking_number ?? null,
      };

      const cartForSupabase: SupabaseCartItem[] = cart.map((item) => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image_url: item.image_url,
      }));

      // ✅ IMPORTANT: send totals (cents) to backend
      const saved = await saveOrderToSupabase(cartForSupabase, shippingDataToSave, totals, null);

      if (!saved?.orderId) {
        throw new Error("Order saved but no orderId was returned.");
      }

      // cleanup
      clearCart();
      localStorage.removeItem("shippingData");
      localStorage.removeItem("shippingRate");
      localStorage.removeItem("stripeTotals");
      localStorage.removeItem("stripeClientSecret");

      const receiptPath = saved.receiptToken
        ? `/receipt/${saved.orderId}?token=${encodeURIComponent(saved.receiptToken)}`
        : `/receipt/${saved.orderId}`;

      navigate(receiptPath);
    } catch (err: any) {
      setMessage(
        err?.message || "An error occurred during checkout. Please try again or contact support."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white/90 backdrop-blur p-6 rounded shadow-md space-y-4 border border-[rgba(92,47,47,0.15)]"
    >
      <div className="text-sm text-[#5c2f2f] space-y-1">
        {totals ? (
          <div className="text-right">
            <div>Subtotal: ${centsToDollars(totals.subtotal_cents)}</div>
            <div>Shipping: ${centsToDollars(totals.shipping_cents)}</div>
            <div>Tax: ${centsToDollars(totals.tax_cents)}</div>
            <div className="font-bold text-base">Total: ${centsToDollars(totals.total_cents)}</div>
          </div>
        ) : (
          <div className="text-right">Totals: —</div>
        )}
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-[#5c2f2f]">
          Cardholder Name
        </label>
        <input
          type="text"
          value={cardName}
          onChange={(e) => setCardName(e.target.value)}
          required
          className="w-full border border-[#d8bcbc] rounded px-3 py-2 placeholder:text-[#c7a5a5] focus:outline-none focus:ring-2 focus:ring-[#5c2f2f]"
          placeholder="Full name on card"
        />
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-[#5c2f2f]">Card Details</label>
        <div className="border border-[#d8bcbc] rounded p-3 bg-white">
          <CardElement options={CARD_OPTIONS} />
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || !shippingRate || !totals || !resolvedClientSecret || isLoading}
        className={`w-full py-2 rounded font-semibold transition ${
          !stripe || !shippingRate || !totals || !resolvedClientSecret || isLoading
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-[#5c2f2f] hover:bg-[#3f1f1f] text-white"
        }`}
      >
        {isLoading ? "Processing..." : `Pay $${displayTotal}`}
      </button>

      {message && <p className="text-center text-[#a25d5d] mt-2">{message}</p>}
    </form>
  );
};

export default StripeCheckoutForm;
